// Mounts the application shell into #app and wires the regions together.

import { effect } from '@/core/signals';
import {
  theme,
  state as appState,
  undo,
  redo,
  toggleTheme,
  togglePlay,
  setTime,
  pause,
} from '@/core/state';
import { createPipeline } from '@/core/pipeline';
import { el, on } from '@/lib/dom';
import { importFile, importBlob } from '@/lib/imageImport';
import { mountToolbar } from './toolbar';
import { mountLayerList } from './layerList';
import { mountInspector } from './inspector';
import { mountViewport, type CursorInfo } from './viewport';
import { toggleCheatsheet } from './cheatsheet';
import { openVariations } from './variations';
import { openPresetGallery } from './presetGallery';
import { openDslEditor } from './dslEditor';
import { openExportDialog } from './exportDialog';
import { openEffectsBrowser } from './effectsBrowser';
import { surpriseMe } from '@/lib/surprise';
import { toggleFullscreen, toggleZen, exitZen, isZen } from '@/lib/fullscreen';
import { toggleStudio } from '@/lib/studioMode';
import { watchStudio } from './studio/studio';

export function mountShell(root: HTMLElement): () => void {
  const top = el('div', { class: 'region-top tb' });
  const left = el('div', { class: 'region-left ll' });
  const center = el('div', { class: 'region-center', id: 'viewport-stage' });
  const right = el('div', { class: 'region-right ins-host' });
  const bottom = el('div', { class: 'region-bottom bs' });
  root.replaceChildren(top, left, center, right, bottom);

  // Theme mirror onto <html>
  const disposeTheme = effect(() => {
    document.documentElement.dataset.theme = theme.get();
  });

  // Bottom strip layout: play controls (left) · cursor (center) · stats (right)
  const playBtn = el('button', { class: 'btn-icon bs-play', ariaLabel: 'Play / pause (P)', title: 'Play / pause (P)', innerHTML: PLAY_ICON });
  const frameNum = el('span', { class: 'bs-frame mono' }, ['t=0']);
  const cursor = el('span', { class: 'bs-cursor mono' });
  const stats = el('span', { class: 'bs-stats mono' });
  bottom.append(playBtn, frameNum, cursor, stats);
  on(playBtn, 'click', () => togglePlay());

  const pipeline = createPipeline();
  const viewport = mountViewport(
    center,
    pipeline,
    (text) => { stats.textContent = text; },
    (info: CursorInfo) => {
      cursor.textContent = info ? `${info.x},${info.y}  ${info.hex}` : '';
    },
  );
  const disposeToolbar = mountToolbar(top, viewport);
  const disposeLayerList = mountLayerList(left);
  const disposeInspector = mountInspector(right);

  // Image import: drag-drop on document, paste from clipboard
  const offDragOver = on(document, 'dragover', (ev) => {
    if ((ev as DragEvent).dataTransfer?.types?.includes('Files')) {
      ev.preventDefault();
      document.body.classList.add('drop-target');
    }
  });
  const offDragLeave = on(document, 'dragleave', (ev) => {
    if ((ev as DragEvent).clientX === 0 && (ev as DragEvent).clientY === 0) {
      document.body.classList.remove('drop-target');
    }
  });
  const offDrop = on(document, 'drop', async (ev) => {
    ev.preventDefault();
    document.body.classList.remove('drop-target');
    const files = (ev as DragEvent).dataTransfer?.files;
    if (!files || files.length === 0) return;
    for (const f of Array.from(files)) {
      await importFile(f);
    }
  });
  const offPaste = on(document, 'paste', async (ev) => {
    if (isEditing(ev.target)) return;
    const items = (ev as ClipboardEvent).clipboardData?.items;
    if (!items) return;
    for (const item of Array.from(items)) {
      if (item.type.startsWith('image/')) {
        const blob = item.getAsFile();
        if (blob) await importBlob(blob);
      }
    }
  });

  // Reactive: play icon, frame counter
  const disposeBottom = effect(() => {
    const s = appState.get();
    playBtn.innerHTML = s.paused ? PLAY_ICON : PAUSE_ICON;
    frameNum.textContent = `t=${s.time}`;
  });

  // Global keyboard shortcuts
  const offKeys = on(window, 'keydown', (ev) => {
    const meta = ev.metaKey || ev.ctrlKey;
    const key = ev.key;
    const lower = key.toLowerCase();

    // Cmd/Ctrl combinations
    if (meta && lower === 'z' && !ev.shiftKey) { ev.preventDefault(); undo(); return; }
    if (meta && (lower === 'y' || (lower === 'z' && ev.shiftKey))) { ev.preventDefault(); redo(); return; }
    if (meta && key === '=') { ev.preventDefault(); viewport.zoomIn(); return; }
    if (meta && key === '-') { ev.preventDefault(); viewport.zoomOut(); return; }
    if (meta && key === '0') { ev.preventDefault(); viewport.fit(); return; }

    // Single-key shortcuts (skip while typing)
    if (meta || isEditing(ev.target)) return;

    if (key === '?') { ev.preventDefault(); toggleCheatsheet(); return; }
    if (lower === 't') { ev.preventDefault(); toggleTheme(); return; }
    if (lower === 'p') { ev.preventDefault(); togglePlay(); return; }
    if (lower === 'r') { ev.preventDefault(); pause(); setTime(0); return; }
    if (lower === 'x') { ev.preventDefault(); surpriseMe(); return; }
    if (lower === 'g') { ev.preventDefault(); openPresetGallery(); return; }
    if (lower === 'd') { ev.preventDefault(); openDslEditor(); return; }
    if (lower === 'e') { ev.preventDefault(); openExportDialog(); return; }
    if (lower === 'a') {
      ev.preventDefault();
      const addBtn = document.querySelector('.ll-add') as HTMLElement | null;
      addBtn?.click();
      return;
    }
    if (lower === 'b') { ev.preventDefault(); openEffectsBrowser(); return; }
    if (lower === 'f') { ev.preventDefault(); toggleFullscreen(); return; }
    if (lower === 'm') { ev.preventDefault(); toggleStudio(); return; }
    if (lower === 'z') {
      ev.preventDefault();
      if (isZen.peek()) exitZen();
      else toggleZen();
      return;
    }
  });
  const disposeStudio = watchStudio();
  void openVariations; // exported via toolbar

  return () => {
    disposeTheme();
    disposeBottom();
    disposeToolbar();
    disposeLayerList();
    disposeInspector();
    disposeStudio();
    offKeys();
    offDragOver();
    offDragLeave();
    offDrop();
    offPaste();
    viewport.dispose();
  };
}

function isEditing(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

const PLAY_ICON = '<svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor" stroke="none" aria-hidden="true"><path d="M4 3l9 5-9 5V3z"/></svg>';
const PAUSE_ICON = '<svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor" stroke="none" aria-hidden="true"><rect x="4" y="3" width="3" height="10"/><rect x="9" y="3" width="3" height="10"/></svg>';
